import { RocheUIHeroTeaser, RocheUIImageRatio } from '@repo/roche-ui';
import { TrackingService } from '@rocheglobal/component-library-dot-com/src/services/tracking';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BaseBlok, BlokToComponentProps } from '../../helpers/blok-to-component/types';
import { ConditionalWrapper } from '../../helpers/conditional-wrapper';
import { useTranslation } from '../../hooks/use-translation';
import { StringService } from '../../services/string';
import { TeaserService } from '../../services/teaser';
import { PageItem } from '../../services/teaser/types';
import { parseAssetSource } from '../../utils/asset';
import { StoryblokService } from '../../services/storyblok';
import { RocheButton } from './roche-button';
import { getAltText } from './alt-text-helper';
import { settings } from '../../helpers/roche-video-player-settings';
import { rocheVideoPlayerTracking, TrackedPercentage } from '../../helpers/roche-video-player-tracking';

type RocheHeroTeaserInternalPageLink = {
  story: {
    name: string;
    id: number;
    url: string;
    full_slug: string;
    preRenderContent?: PageItem;
  };
};

// remove at some point as it is unnecessary
// (use empty overline as indicator for not showing overline)
export type RocheHeroTeaserLayout = 'teaser' | 'teaser-without-overline';

export type RocheHeroTeaserImage = {
  brightcove_id?: string;
  src?: { filename: string; alt: string };
  srcAltText?: string;
  ratio?: string;
}[];

export type RocheHeroTeaserType = {
  layout?: RocheHeroTeaserLayout;
  left_icon?: string;
  mirror_layout?: boolean;
  page: RocheHeroTeaserInternalPageLink;
  right_icon?: string;
  slotted_secondary_action?: any[];
  teaser_image_ratio: string;
  button_title?: string;
  slotted_hero_image?: RocheHeroTeaserImage;
};

type RocheHeroTeaserProps = BlokToComponentProps<BaseBlok & RocheHeroTeaserType>;

export const RocheHeroTeaser = ({
  keyForChildren,
  wrapperTag,
  updateHoistedState,
  getComponent,
  slot,
  blok,
}: RocheHeroTeaserProps): JSX.Element => {
  const Wrapper = wrapperTag as React.ElementType;
  const [teaser, setTeaser] = useState<PageItem>(blok.page?.story?.preRenderContent || ({} as PageItem));
  const teaserOverline = blok.layout === 'teaser-without-overline' ? undefined : teaser?.content?.teaser_overline;
  const primaryActionUrl = StringService.sanitizeUrl(`/${teaser?.full_slug}`);

  const readMoreTranslation = useTranslation('read more');
  const playLabel = useTranslation('play');
  const pauseLabel = useTranslation('pause');
  const trackedVideoPercentages = useRef<TrackedPercentage>({});
  const primaryButtonText =
    blok.button_title !== '' && blok.button_title ? blok.button_title : StringService.capitalize(readMoreTranslation);

  // See roche-hero-teaser.ts
  // The schema allows only a roche-button here and a max of 1. definition.
  const secondaryButtonSlot = blok.slotted_secondary_action[0];
  const secondaryButton = secondaryButtonSlot ? (
    <RocheButton getComponent={getComponent} blok={secondaryButtonSlot} hasMarginTop={false} />
  ) : undefined;

  const imageUrl = (input?: RocheHeroTeaserImage[number]): string | null => {
    return parseAssetSource(input?.src?.filename) ?? input?.brightcove_id;
  };

  const imageOverwriteSource = useMemo(() => imageUrl(blok.slotted_hero_image[0]), [blok.slotted_hero_image]);

  const teaserImageSource = useMemo(() => imageUrl(teaser.content?.teaser_image?.[0]), [teaser?.content?.teaser_image]);

  const imageRatio = imageOverwriteSource
    ? (blok.slotted_hero_image[0].ratio as RocheUIImageRatio)
    : (blok.teaser_image_ratio as RocheUIImageRatio);

  const imageAlt = imageOverwriteSource
    ? getAltText(blok.slotted_hero_image[0])
    : getAltText(teaser?.content?.teaser_image?.[0]);

  const trackPrimaryButtonClick = () => {
    TrackingService.push({
      event: 'teaser_click',
      component_name: 'roche-hero-teaser',
      additionalParameters: {
        teaser_title: teaser.content?.teaser_title,
        teaser_url: primaryActionUrl,
      },
    });
  };

  useEffect(() => {
    if ((teaser && Object.keys(teaser).length > 0) || !blok.page?.story?.id) return;

    TeaserService.getTeaser(blok.page?.story.id).then((result) => {
      setTeaser(result);
      // eslint-disable-next-line no-underscore-dangle
      return updateHoistedState?.(1, 1, blok._uid, result);
    });
  }, []);

  if ((!teaser || Object.keys(teaser).length === 0) && StoryblokService.isInEditor()) {
    return renderWarning(keyForChildren, wrapperTag, slot);
  }

  if (!teaser?.content) return null;

  return (
    <ConditionalWrapper
      key={keyForChildren}
      condition={wrapperTag !== undefined && wrapperTag !== ''}
      wrapper={(child: JSX.Element): JSX.Element => <Wrapper slot={slot}>{child}</Wrapper>}
    >
      <RocheUIHeroTeaser
        teaserTitle={teaser.content?.teaser_title}
        teaserOverline={teaserOverline}
        teaserDescription={teaser.content?.teaser_description}
        teaserImage={imageOverwriteSource ?? teaserImageSource ?? ''}
        teaserImageAlt={imageAlt}
        teaserImageRatio={imageRatio}
        primaryButtonText={primaryButtonText}
        primaryButtonUrl={primaryActionUrl}
        onPrimaryButtonClick={trackPrimaryButtonClick}
        secondaryButton={secondaryButton}
        leftIcon={blok.left_icon}
        rightIcon={blok.right_icon}
        imageOnLeftSide={blok.mirror_layout}
        brightcoveSettings={settings.brightcove}
        playLabel={playLabel}
        pauseLabel={pauseLabel}
        onVideoStateChange={(event) => rocheVideoPlayerTracking(event, trackedVideoPercentages, true)}
      />
    </ConditionalWrapper>
  );
};

const renderWarning = (keyForChildren: string | number, wrapperTag: string, slot: string) => {
  const Wrapper = wrapperTag as React.ElementType;
  const description = `
    If you wish to proceed with this action, please publish the linked page first.
    Note that if the linked page on the teaser remains unpublished,
    this teaser will not show up on the live version of the page.
  `;
  return (
    // ConditionalWrapper is needed to work within <roche-slider-item>
    <ConditionalWrapper
      key={keyForChildren}
      condition={wrapperTag !== undefined && wrapperTag !== ''}
      wrapper={(children: JSX.Element): JSX.Element => <Wrapper slot={slot}>{children}</Wrapper>}
    >
      <RocheUIHeroTeaser
        teaserTitle={'WARNING: This teaser is not published!'}
        teaserDescription={description}
        teaserImageRatio={RocheUIImageRatio.HD}
        unpublished={true}
      />
    </ConditionalWrapper>
  );
};
